/**
 * CleaningWorkflowModal.js - Complete Cleaning Service Workflow
 * Steps: Arrival → Checklist → Before Photos → In Progress → After Photos → Signature → Complete
 */
import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronRight, ChevronLeft, MapPin, Clock, Camera, Pen, 
  CheckCircle, AlertTriangle, User, Phone, Home, Sparkles,
  Loader2, CheckSquare, Square, Navigation, Timer, Image, Droplets
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const CLEANING_STEPS = {
  OVERVIEW: 'overview',
  ARRIVAL: 'arrival',
  WALKTHROUGH: 'walkthrough',
  PHOTOS_BEFORE: 'photos_before',
  CLEANING: 'cleaning',
  PHOTOS_AFTER: 'photos_after',
  FINAL_CHECK: 'final_check',
  SIGNATURE: 'signature',
  COMPLETE: 'complete'
};

const CleaningWorkflowModal = ({ isOpen, onClose, booking, worker, onWorkflowComplete }) => {
  const [currentStep, setCurrentStep] = useState(CLEANING_STEPS.OVERVIEW);
  const [loading, setLoading] = useState(false);
  
  // Arrival state
  const [arrived, setArrived] = useState(false);
  const [arrivalTime, setArrivalTime] = useState(null);
  
  // Walkthrough state
  const [walkthroughComplete, setWalkthroughComplete] = useState(false);
  const [customerPresent, setCustomerPresent] = useState(true);
  const [additionalNotes, setAdditionalNotes] = useState('');
  
  // Photo state
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Cleaning checklist - dynamic based on service
  const [checklist, setChecklist] = useState(() => {
    const serviceName = (booking?.service_name || '').toLowerCase();
    const items = [
      // Living Areas
      { id: 'dust_surfaces', label: 'Dust all surfaces, shelves, decor', checked: false, category: 'Living Areas' },
      { id: 'vacuum_carpets', label: 'Vacuum all carpets and rugs', checked: false, category: 'Living Areas' },
      { id: 'mop_floors', label: 'Mop hard floors', checked: false, category: 'Living Areas' },
      { id: 'wipe_switches', label: 'Wipe light switches and door handles', checked: false, category: 'Living Areas' },
      
      // Kitchen
      { id: 'clean_counters', label: 'Clean and sanitize countertops', checked: false, category: 'Kitchen' },
      { id: 'clean_appliances', label: 'Clean appliance exteriors', checked: false, category: 'Kitchen' },
      { id: 'clean_sink', label: 'Clean and sanitize sink', checked: false, category: 'Kitchen' },
      { id: 'empty_trash', label: 'Empty trash and replace liner', checked: false, category: 'Kitchen' },
      
      // Bathrooms
      { id: 'clean_toilet', label: 'Clean and sanitize toilet', checked: false, category: 'Bathrooms' },
      { id: 'clean_shower', label: 'Clean shower/tub', checked: false, category: 'Bathrooms' },
      { id: 'clean_vanity', label: 'Clean vanity and mirror', checked: false, category: 'Bathrooms' },
      { id: 'clean_bath_floor', label: 'Clean bathroom floor', checked: false, category: 'Bathrooms' },
      
      // Bedrooms
      { id: 'make_beds', label: 'Make beds / change linens', checked: false, category: 'Bedrooms' },
      { id: 'dust_bedroom', label: 'Dust bedroom furniture', checked: false, category: 'Bedrooms' },
      { id: 'vacuum_bedroom', label: 'Vacuum bedroom floors', checked: false, category: 'Bedrooms' }
    ];
    
    // Deep clean additions
    if (serviceName.includes('deep')) {
      items.push(
        { id: 'clean_baseboards', label: 'Clean baseboards', checked: false, category: 'Deep Clean' },
        { id: 'clean_inside_fridge', label: 'Clean inside refrigerator', checked: false, category: 'Deep Clean' },
        { id: 'clean_inside_oven', label: 'Clean inside oven', checked: false, category: 'Deep Clean' },
        { id: 'clean_windows', label: 'Clean interior windows', checked: false, category: 'Deep Clean' },
        { id: 'clean_cabinets', label: 'Wipe cabinet exteriors', checked: false, category: 'Deep Clean' },
        { id: 'clean_blinds', label: 'Dust blinds/window treatments', checked: false, category: 'Deep Clean' }
      );
    }
    
    // Move-out additions
    if (serviceName.includes('move')) {
      items.push(
        { id: 'clean_closets', label: 'Clean inside closets', checked: false, category: 'Move Out' },
        { id: 'clean_drawers', label: 'Wipe inside drawers/cabinets', checked: false, category: 'Move Out' },
        { id: 'clean_garage', label: 'Sweep garage (if included)', checked: false, category: 'Move Out' }
      );
    }
    
    return items;
  });
  
  // Signature state
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Completion notes
  const [completionNotes, setCompletionNotes] = useState('');
  const [cleaningStartTime, setCleaningStartTime] = useState(null);
  const [cleaningEndTime, setCleaningEndTime] = useState(null);

  // Toggle checklist item
  const toggleChecklistItem = (id) => {
    setChecklist(prev => prev.map(item =>
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  // Handle photo upload
  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      const photoObj = {
        id: Date.now(),
        url: previewUrl,
        file: file,
        timestamp: new Date().toISOString(),
        room: 'General'
      };

      if (type === 'before') {
        setBeforePhotos(prev => [...prev, photoObj]);
      } else {
        setAfterPhotos(prev => [...prev, photoObj]);
      }
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to add photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Signature handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#D4AF37';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        setSignatureData(canvas.toDataURL());
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  // GPS arrival
  const handleArrival = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {
          setArrived(true);
          setArrivalTime(new Date().toISOString());
          toast.success('Arrival confirmed');
        },
        () => {
          setArrived(true);
          setArrivalTime(new Date().toISOString());
          toast.info('Arrival confirmed manually');
        }
      );
    } else {
      setArrived(true);
      setArrivalTime(new Date().toISOString());
    }
  };

  // Start cleaning
  const startCleaning = () => {
    setCleaningStartTime(new Date().toISOString());
    toast.success('Cleaning started - timer running');
  };

  // Complete workflow
  const handleComplete = async () => {
    setLoading(true);
    setCleaningEndTime(new Date().toISOString());
    
    try {
      // Prepare completion data for the new job-completion API
      const completionData = {
        job_id: booking.id,
        service_type: 'cleaning',
        photos: [
          ...beforePhotos.map(p => ({
            photo_data: p.data || p.url,
            photo_type: 'before',
            caption: 'Before cleaning'
          })),
          ...afterPhotos.map(p => ({
            photo_data: p.data || p.url,
            photo_type: 'after',
            caption: 'After cleaning'
          }))
        ],
        signature_data: signatureData,
        signer_name: signerName,
        completion_notes: `${completionNotes}${additionalNotes ? '\n' + additionalNotes : ''}`.trim(),
        customer_satisfied: true,
        checklist_completed: checklist.filter(c => c.checked).length,
        checklist_total: checklist.length,
        arrival_time: arrivalTime,
        start_time: cleaningStartTime,
        end_time: new Date().toISOString()
      };

      // Try to submit to job-completion API (photos & signature upload)
      try {
        const token = localStorage.getItem('auth_token');
        if (token && (beforePhotos.length > 0 || afterPhotos.length > 0 || signatureData)) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-completion/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(completionData)
          });
        }
      } catch (uploadErr) {
        console.warn('Photo/signature upload failed, continuing with completion:', uploadErr);
      }

      toast.success('Cleaning job completed!');
      onWorkflowComplete?.(completionData);
      onClose();
    } catch (error) {
      toast.error('Failed to complete job');
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const goToNextStep = () => {
    const stepOrder = Object.values(CLEANING_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const goToPrevStep = () => {
    const stepOrder = Object.values(CLEANING_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const getStepProgress = () => {
    const stepOrder = Object.values(CLEANING_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    return ((currentIndex + 1) / stepOrder.length) * 100;
  };

  // Group checklist by category
  const checklistByCategory = checklist.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        data-testid="cleaning-workflow-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[var(--card)] border border-[var(--border)] rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-4 border-b border-[var(--border)] bg-gradient-to-r from-blue-600/20 to-indigo-600/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Cleaning Workflow</h2>
                  <p className="text-xs text-gray-400">{booking?.service_name || 'Cleaning Service'}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Progress</span>
                <span>{Math.round(getStepProgress())}%</span>
              </div>
              <div className="h-2 bg-[var(--background)] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-blue-500 to-indigo-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${getStepProgress()}%` }}
                />
              </div>
            </div>

            {/* Timer Display */}
            {cleaningStartTime && !cleaningEndTime && (
              <div className="mt-2 flex items-center gap-2 text-green-400 text-sm">
                <Timer className="w-4 h-4 animate-pulse" />
                <span>Cleaning in progress...</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <AnimatePresence mode="wait">
              {/* OVERVIEW STEP */}
              {currentStep === CLEANING_STEPS.OVERVIEW && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white">Job Details</h3>
                  
                  <div className="p-3 rounded-lg bg-[var(--background)] space-y-2">
                    <div className="flex items-center gap-2 text-white">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{booking?.customer_name || 'Customer'}</span>
                    </div>
                    {booking?.customer_phone && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Phone className="w-4 h-4" />
                        <span>{booking.customer_phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-xs text-blue-400 mb-1 flex items-center gap-1">
                      <Home className="w-3 h-3" /> ADDRESS
                    </p>
                    <p className="text-white">{booking?.address || 'Address not specified'}</p>
                  </div>

                  {/* Property Details */}
                  {(booking?.cleaning_details || booking?.property_details) && (
                    <div className="p-3 rounded-lg bg-[var(--background)] space-y-2">
                      <p className="text-xs text-gray-400">PROPERTY DETAILS</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {booking?.cleaning_details?.bedrooms && (
                          <div className="text-white">
                            <span className="text-gray-400">Bedrooms:</span> {booking.cleaning_details.bedrooms}
                          </div>
                        )}
                        {booking?.cleaning_details?.bathrooms && (
                          <div className="text-white">
                            <span className="text-gray-400">Bathrooms:</span> {booking.cleaning_details.bathrooms}
                          </div>
                        )}
                        {booking?.cleaning_details?.sqft && (
                          <div className="text-white">
                            <span className="text-gray-400">Sq Ft:</span> {booking.cleaning_details.sqft}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {booking?.special_instructions && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-xs text-yellow-400 mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> SPECIAL INSTRUCTIONS
                      </p>
                      <p className="text-white text-sm">{booking.special_instructions}</p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* ARRIVAL STEP */}
              {currentStep === CLEANING_STEPS.ARRIVAL && (
                <motion.div
                  key="arrival"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-400" />
                    Arrival
                  </h3>

                  <div className="p-4 rounded-lg bg-[var(--background)]">
                    <p className="text-white mb-4">{booking?.address}</p>
                    
                    {!arrived ? (
                      <Button 
                        onClick={handleArrival}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        Confirm Arrival
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 text-green-400 p-3 rounded bg-green-500/10">
                        <CheckCircle className="w-5 h-5" />
                        <span>Arrived at {new Date(arrivalTime).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* WALKTHROUGH STEP */}
              {currentStep === CLEANING_STEPS.WALKTHROUGH && (
                <motion.div
                  key="walkthrough"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white">Customer Walkthrough</h3>

                  <div
                    onClick={() => setCustomerPresent(!customerPresent)}
                    className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer ${
                      customerPresent ? 'bg-green-500/10 border border-green-500/30' : 'bg-[var(--background)]'
                    }`}
                  >
                    {customerPresent ? (
                      <CheckSquare className="w-5 h-5 text-green-400" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-white">Customer is present</span>
                  </div>

                  {customerPresent && (
                    <div
                      onClick={() => setWalkthroughComplete(!walkthroughComplete)}
                      className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer ${
                        walkthroughComplete ? 'bg-green-500/10 border border-green-500/30' : 'bg-[var(--background)]'
                      }`}
                    >
                      {walkthroughComplete ? (
                        <CheckSquare className="w-5 h-5 text-green-400" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="text-white">Completed walkthrough with customer</span>
                    </div>
                  )}

                  <div>
                    <Label className="text-gray-400">Notes from walkthrough</Label>
                    <Textarea
                      value={additionalNotes}
                      onChange={(e) => setAdditionalNotes(e.target.value)}
                      placeholder="Any special requests or areas of focus..."
                      className="mt-1 bg-[var(--background)]"
                      rows={3}
                    />
                  </div>
                </motion.div>
              )}

              {/* PHOTOS BEFORE STEP */}
              {currentStep === CLEANING_STEPS.PHOTOS_BEFORE && (
                <motion.div
                  key="photos_before"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Camera className="w-5 h-5 text-yellow-400" />
                    Before Photos
                  </h3>

                  <p className="text-gray-400 text-sm">
                    Take photos of each room/area before cleaning
                  </p>

                  <div className="grid grid-cols-3 gap-2">
                    {beforePhotos.map(photo => (
                      <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                        <img src={photo.url} alt="Before" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    
                    <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--border)] flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)]">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e, 'before')}
                        disabled={uploadingPhoto}
                      />
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Add Photo</span>
                        </>
                      )}
                    </label>
                  </div>
                </motion.div>
              )}

              {/* CLEANING STEP */}
              {currentStep === CLEANING_STEPS.CLEANING && (
                <motion.div
                  key="cleaning"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-blue-400" />
                      Cleaning Checklist
                    </h3>
                    
                    {!cleaningStartTime && (
                      <Button size="sm" onClick={startCleaning} className="bg-green-600 hover:bg-green-700">
                        <Timer className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    )}
                  </div>

                  <div className="space-y-4">
                    {Object.entries(checklistByCategory).map(([category, items]) => (
                      <div key={category}>
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">{category}</p>
                        <div className="space-y-1">
                          {items.map(item => (
                            <div
                              key={item.id}
                              onClick={() => toggleChecklistItem(item.id)}
                              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                item.checked ? 'bg-green-500/10' : 'bg-[var(--background)] hover:bg-[var(--card)]'
                              }`}
                            >
                              {item.checked ? (
                                <CheckSquare className="w-4 h-4 text-green-400 shrink-0" />
                              ) : (
                                <Square className="w-4 h-4 text-gray-400 shrink-0" />
                              )}
                              <span className={`text-sm ${item.checked ? 'text-green-300' : 'text-white'}`}>
                                {item.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-sm text-blue-300">
                      {checklist.filter(c => c.checked).length} of {checklist.length} tasks completed
                    </p>
                  </div>
                </motion.div>
              )}

              {/* PHOTOS AFTER STEP */}
              {currentStep === CLEANING_STEPS.PHOTOS_AFTER && (
                <motion.div
                  key="photos_after"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Image className="w-5 h-5 text-green-400" />
                    After Photos
                  </h3>

                  <p className="text-gray-400 text-sm">
                    Take photos of each cleaned room/area
                  </p>

                  <div className="grid grid-cols-3 gap-2">
                    {afterPhotos.map(photo => (
                      <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                        <img src={photo.url} alt="After" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    
                    <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--border)] flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)]">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e, 'after')}
                        disabled={uploadingPhoto}
                      />
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Add Photo</span>
                        </>
                      )}
                    </label>
                  </div>
                </motion.div>
              )}

              {/* FINAL CHECK STEP */}
              {currentStep === CLEANING_STEPS.FINAL_CHECK && (
                <motion.div
                  key="final_check"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white">Final Walkthrough</h3>

                  <div className="p-4 rounded-lg bg-[var(--background)] space-y-3">
                    <p className="text-sm text-gray-300">
                      Do a final walkthrough of the property to ensure everything is clean and in order.
                    </p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        <span>All rooms cleaned</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        <span>Trash emptied</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        <span>Supplies packed up</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        <span>Doors/windows secured</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-400">Completion Notes</Label>
                    <Textarea
                      value={completionNotes}
                      onChange={(e) => setCompletionNotes(e.target.value)}
                      placeholder="Any notes about the cleaning..."
                      className="mt-1 bg-[var(--background)]"
                      rows={3}
                    />
                  </div>
                </motion.div>
              )}

              {/* SIGNATURE STEP */}
              {currentStep === CLEANING_STEPS.SIGNATURE && (
                <motion.div
                  key="signature"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Pen className="w-5 h-5 text-yellow-400" />
                    Customer Signature
                  </h3>

                  <div>
                    <Label className="text-gray-400">Customer Name</Label>
                    <Input
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Enter customer name"
                      className="mt-1 bg-[var(--background)]"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400">Signature</Label>
                    <div className="mt-1 border-2 border-dashed border-[var(--border)] rounded-lg overflow-hidden bg-white">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className="w-full touch-none cursor-crosshair"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={clearSignature} className="mt-2">
                      Clear
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* COMPLETE STEP */}
              {currentStep === CLEANING_STEPS.COMPLETE && (
                <motion.div
                  key="complete"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Ready to Complete</h3>
                  </div>

                  <div className="space-y-2 p-4 rounded-lg bg-[var(--background)]">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Before Photos</span>
                      <span className="text-white">{beforePhotos.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">After Photos</span>
                      <span className="text-white">{afterPhotos.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Checklist</span>
                      <span className="text-white">{checklist.filter(c => c.checked).length}/{checklist.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Signature</span>
                      <span className={signatureData ? 'text-green-400' : 'text-yellow-400'}>
                        {signatureData ? 'Collected' : 'Pending'}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleComplete}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Complete Cleaning Job
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[var(--border)] flex justify-between">
            <Button
              variant="outline"
              onClick={goToPrevStep}
              disabled={currentStep === CLEANING_STEPS.OVERVIEW}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {currentStep !== CLEANING_STEPS.COMPLETE && (
              <Button onClick={goToNextStep}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CleaningWorkflowModal;
